class FacetFiltersForm extends HTMLElement {
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 800);

        const facetForm = this.querySelector('form');
        facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

        const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
        if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) return;
            FacetFiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);
    }

    static toggleActiveFacets(disable = true) {
        document.querySelectorAll('.js-facet-remove').forEach((element) => {
            element.classList.toggle('disabled', disable);
        });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        const countContainer = document.getElementById('ProductCount');
        const countContainerDesktop = document.getElementById('ProductCountDesktop');
        const loadingSpinners = document.querySelectorAll(
            '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
        document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
        if (countContainer) {
            countContainer.classList.add('loading');
        }
        if (countContainerDesktop) {
            countContainerDesktop.classList.add('loading');
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FacetFiltersForm.filterData.some(filterDataUrl)
                ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
                : FacetFiltersForm.renderSectionFromFetch(url, event);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
                FacetFiltersForm.renderFilters(html, event);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
                if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    }

    static renderProductGridContainer(html) {
        document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById('ProductGridContainer').innerHTML;

        document
            .getElementById('ProductGridContainer')
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => {
                element.classList.add('scroll-trigger--cancel');
            });
    }

    static renderProductCount(html) {
        const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
        const container = document.getElementById('ProductCount');
        const containerDesktop = document.getElementById('ProductCountDesktop');
        container.innerHTML = count;
        container.classList.remove('loading');
        if (containerDesktop) {
            containerDesktop.innerHTML = count;
            containerDesktop.classList.remove('loading');
        }
        const loadingSpinners = document.querySelectorAll(
            '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }

    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
            '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
        );
        const facetDetailsElementsFromDom = document.querySelectorAll(
            '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
        );

        // Remove facets that are no longer returned from the server
        Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
            if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
                currentElement.remove();
            }
        });

        const matchesId = (element) => {
            const jsFilter = event ? event.target.closest('.js-filter') : undefined;
            return jsFilter ? element.id === jsFilter.id : false;
        };

        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

        facetsToRender.forEach((elementToRender, index) => {
            const currentElement = document.getElementById(elementToRender.id);
            // Element already rendered in the DOM so just update the innerHTML
            if (currentElement) {
                document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
            } else {
                if (index > 0) {
                    const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
                    // Same facet type (eg horizontal/vertical or drawer/mobile)
                    if (elementToRender.className === previousElementClassName) {
                        document.getElementById(previousElementId).after(elementToRender);
                        return;
                    }
                }

                if (elementToRender.parentElement) {
                    document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
                }
            }
        });

        FacetFiltersForm.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
            const closestJSFilterID = event.target.closest('.js-filter').id;

            if (closestJSFilterID) {
                FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
                FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

                const newFacetDetailsElement = document.getElementById(closestJSFilterID);
                const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
                    ? `.mobile-facets__close-button`
                    : `.facets__summary`;
                const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

                const isTextInput = event.target.getAttribute('type') === 'text';

                if (newElementToActivate && !isTextInput) newElementToActivate.focus();
            }
        }
    }

    static renderActiveFacets(html) {
        const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

        activeFacetElementSelectors.forEach((selector) => {
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) return;
            document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
        });

        FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
        const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
        });

        document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    }

    static renderCounts(source, target) {
        const targetSummary = target.querySelector('.facets__summary');
        const sourceSummary = source.querySelector('.facets__summary');

        if (sourceSummary && targetSummary) {
            targetSummary.outerHTML = sourceSummary.outerHTML;
        }

        const targetHeaderElement = target.querySelector('.facets__header');
        const sourceHeaderElement = source.querySelector('.facets__header');

        if (sourceHeaderElement && targetHeaderElement) {
            targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
        }

        const targetWrapElement = target.querySelector('.facets-wrap');
        const sourceWrapElement = source.querySelector('.facets-wrap');

        if (sourceWrapElement && targetWrapElement) {
            const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
            if (isShowingMore) {
                sourceWrapElement
                    .querySelectorAll('.facets__item.hidden')
                    .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
            }

            targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
        }
    }

    static renderMobileCounts(source, target) {
        const targetFacetsList = target.querySelector('.mobile-facets__list');
        const sourceFacetsList = source.querySelector('.mobile-facets__list');

        if (sourceFacetsList && targetFacetsList) {
            targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    static getSections() {
        return [
            {
                section: document.getElementById('product-grid').dataset.id,
            },
        ];
    }

    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }

    onSubmitForm(searchParams, event) {
        FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('facet-filters-form form');
        if (event.srcElement.className == 'mobile-facets__checkbox') {
            const searchParams = this.createSearchParams(event.target.closest('form'));
            this.onSubmitForm(searchParams, event);
        } else {
            const forms = [];
            const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

            sortFilterForms.forEach((form) => {
                if (!isMobile) {
                    if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
                        forms.push(this.createSearchParams(form));
                    }
                } else if (form.id === 'FacetFiltersFormMobile') {
                    forms.push(this.createSearchParams(form));
                }
            });
            this.onSubmitForm(forms.join('&'), event);
        }
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        const url =
            event.currentTarget.href.indexOf('?') == -1
                ? ''
                : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(url);
    }
}


// Price Filter

// FacetFiltersForm.filterData = [];
// FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
// FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// customElements.define('facet-filters-form', FacetFiltersForm);
// FacetFiltersForm.setListeners();

// class PriceRange extends HTMLElement {
//   constructor() {
//     super();

//     // Slider inputs
//     this.minSlider = document.getElementById('price-slider-1-min');
//     this.maxSlider = document.getElementById('price-slider-1-max');

//     // Text inputs (adjust IDs to match your HTML)
//     this.minText = document.querySelector('input[id$="-GTE"]');
//     this.maxText = document.querySelector('input[id$="-LTE"]');

//     // Track element
//     this.track = document.getElementById('price-slider-1-track');

//     // Original range
//     this.originalMin = parseInt(this.minSlider.dataset.min) || 0;
//     this.originalMax = parseInt(this.maxSlider.dataset.max) || 1000;

//     // Event listeners
//     this.minSlider.addEventListener('input', () => this.onSliderChange('min'));
//     this.maxSlider.addEventListener('input', () => this.onSliderChange('max'));
//     this.minText.addEventListener('change', () => this.onTextChange('min'));
//     this.maxText.addEventListener('change', () => this.onTextChange('max'));

//     this.updateValues();
//   }

//   parseValue(val) {
//     return parseInt(val.toString().replace(/,/g, '')) || 0;
//   }

//   onSliderChange(which) {
//     let minVal = this.parseValue(this.minSlider.value);
//     let maxVal = this.parseValue(this.maxSlider.value);

//     // enforce minimum gap
//     if (maxVal - minVal < 5) {
//       if (which === 'min') minVal = maxVal - 5;
//       else maxVal = minVal + 5;
//     }

//     this.minSlider.value = minVal;
//     this.maxSlider.value = maxVal;

//     // update text inputs
//     this.minText.value = minVal;
//     this.maxText.value = maxVal;

//     this.updateTrack();
//   }

//   onTextChange(which) {
//     let minVal = this.parseValue(this.minText.value);
//     let maxVal = this.parseValue(this.maxText.value);

//     // Clamp values to slider min/max
//     minVal = Math.max(this.originalMin, Math.min(minVal, this.originalMax));
//     maxVal = Math.max(this.originalMin, Math.min(maxVal, this.originalMax));

//     // enforce gap
//     if (maxVal - minVal < 5) {
//       if (which === 'min') minVal = maxVal - 5;
//       else maxVal = minVal + 5;
//     }

//     this.minSlider.value = minVal;
//     this.maxSlider.value = maxVal;
//     this.minText.value = minVal;
//     this.maxText.value = maxVal;

//     this.updateTrack();
//   }

//   updateTrack() {
//     const minPercent = ((parseInt(this.minSlider.value) - this.originalMin) / (this.originalMax - this.originalMin)) * 100;
//     const maxPercent = ((parseInt(this.maxSlider.value) - this.originalMin) / (this.originalMax - this.originalMin)) * 100;

//     if (this.track) {
//       this.track.style.left = minPercent + '%';
//       this.track.style.width = (maxPercent - minPercent) + '%';
//     }
//   }

//   updateValues() {
//     // Initialize track and text inputs
//     this.minText.value = this.minSlider.value;
//     this.maxText.value = this.maxSlider.value;
//     this.updateTrack();
//   }
// }

// customElements.define('price-range', PriceRange);

// class FacetRemove extends HTMLElement {
//   constructor() {
//     super();
//     const facetLink = this.querySelector('a');
//     facetLink.setAttribute('role', 'button');
//     facetLink.addEventListener('click', this.closeFilter.bind(this));
//     facetLink.addEventListener('keyup', (event) => {
//       event.preventDefault();
//       if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
//     });
//   }

//   closeFilter(event) {
//     event.preventDefault();
//     const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
//     form.onActiveFilterClick(event);
//   }
// }

// customElements.define('facet-remove', FacetRemove);



// Fixed Price Facet code  ---- Also Worked In Mobile Too. **************


class PriceRange extends HTMLElement {
    connectedCallback() {
        this.minSlider = this.querySelector('input[data-role="min"]');
        this.maxSlider = this.querySelector('input[data-role="max"]');
        this.minText = this.querySelector('input[id$="-GTE"]');
        this.maxText = this.querySelector('input[id$="-LTE"]');
        this.minDisplay = this.querySelector('.min-display');
        this.maxDisplay = this.querySelector('.max-display');
        this.wrapper = this.querySelector('.custom-price-filter');
        this.track = this.querySelector('[id*="track"]');

        if (!this.minSlider || !this.maxSlider || !this.minText || !this.maxText) {
            console.warn('[PriceRange] Could not find inputs inside', this);
            return;
        }

        this.originalMin = parseInt(this.minSlider.min) || 0;
        this.originalMax = parseInt(this.maxSlider.max) || 1000;
        // Was this filter actually set by the user (per Liquid), or is it just showing defaults?
        this.isActive = this.wrapper?.dataset.active === 'true';

        this.minSlider.addEventListener('input', () => this.onSliderChange('min'));
        this.maxSlider.addEventListener('input', () => this.onSliderChange('max'));
        this.minText.addEventListener('change', () => this.onTextChange('min'));
        this.maxText.addEventListener('change', () => this.onTextChange('max'));

        this.updateValues();
    }

    parseValue(val) {
        return parseInt(val.toString().replace(/,/g, '')) || 0;
    }

    formatMoney(value) {
        return `$${value.toLocaleString()}`;
    }

    onSliderChange(which) {
        let minVal = this.parseValue(this.minSlider.value);
        let maxVal = this.parseValue(this.maxSlider.value);

        if (maxVal - minVal < 5) {
            if (which === 'min') minVal = maxVal - 5;
            else maxVal = minVal + 5;
        }

        this.minSlider.value = minVal;
        this.maxSlider.value = maxVal;
        this.minText.value = minVal;
        this.maxText.value = maxVal;
        this.isActive = true; // user has now genuinely touched the price filter

        this.updateTrack();
        this.updateDisplays(minVal, maxVal);

        this.minText.dispatchEvent(new Event('change', { bubbles: true }));
        this.maxText.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onTextChange(which) {
        let minVal = this.parseValue(this.minText.value);
        let maxVal = this.parseValue(this.maxText.value);

        minVal = Math.max(this.originalMin, Math.min(minVal, this.originalMax));
        maxVal = Math.max(this.originalMin, Math.min(maxVal, this.originalMax));

        if (maxVal - minVal < 5) {
            if (which === 'min') minVal = maxVal - 5;
            else maxVal = minVal + 5;
        }

        this.minSlider.value = minVal;
        this.maxSlider.value = maxVal;
        this.minText.value = minVal;
        this.maxText.value = maxVal;
        this.isActive = true;

        this.updateTrack();
        this.updateDisplays(minVal, maxVal);

        this.minText.dispatchEvent(new Event('input', { bubbles: true }));
        this.maxText.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateTrack() {
        if (!this.track) return;
        const minPct = ((parseInt(this.minSlider.value) - this.originalMin) / (this.originalMax - this.originalMin)) * 100;
        const maxPct = ((parseInt(this.maxSlider.value) - this.originalMin) / (this.originalMax - this.originalMin)) * 100;
        this.track.style.left = minPct + '%';
        this.track.style.width = (maxPct - minPct) + '%';
    }

    updateDisplays(minVal, maxVal) {
        if (this.minDisplay) this.minDisplay.textContent = this.formatMoney(minVal);
        if (this.maxDisplay) this.maxDisplay.textContent = this.formatMoney(maxVal);
    }

    updateValues() {
        // Only pre-fill the actual submittable text inputs when this price
        // filter is genuinely active. Otherwise leave them blank so an
        // untouched price slider is never included in the form submission
        // and never gets mistaken for a selected filter.
        if (this.isActive) {
            this.minText.value = this.minSlider.value;
            this.maxText.value = this.maxSlider.value;
        }

        this.updateTrack();
        this.updateDisplays(parseInt(this.minSlider.value), parseInt(this.maxSlider.value));
    }
}


customElements.define('price-range', PriceRange);

// ✅ Register FacetFiltersForm
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

customElements.define('facet-remove', class FacetRemove extends HTMLElement {
    constructor() {
        super();
        const facetLink = this.querySelector('a');
        if (!facetLink) return;
        facetLink.setAttribute('role', 'button');
        facetLink.addEventListener('click', (event) => {
            event.preventDefault();
            const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
            if (form) form.onActiveFilterClick(event);
        });
        facetLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') {
                const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
                if (form) form.onActiveFilterClick(event);
            }
        });
    }
});

/**
 * Called by the Apply button in the mobile/drawer filter.
 * Submits the form via FacetFiltersForm so the price (and any other) filter is applied.
 * @param {HTMLElement} btn - the Apply button element
 */
window.applyFacetFilters = function(btn) {
    // Find the facet-filters-form ancestor
    const facetForm = btn.closest('facet-filters-form');
    if (facetForm) {
        const form = facetForm.querySelector('form');
        if (form) {
            // Trigger a fake input event on the form so debouncedOnSubmit fires
            const fakeEvent = new Event('input', { bubbles: true });
            Object.defineProperty(fakeEvent, 'target', { writable: false, value: form });
            Object.defineProperty(fakeEvent, 'srcElement', { writable: false, value: form.querySelector('input') || form });
            // Use the instance method directly
            const formData = new FormData(form);
            const searchParams = new URLSearchParams(formData).toString();

            // Merge params from all relevant forms (same logic as onSubmitHandler)
            const isMobile = form.id === 'FacetFiltersFormMobile';
            const allForms = document.querySelectorAll('facet-filters-form form');
            const forms = [];
            allForms.forEach((f) => {
                if (!isMobile) {
                    if (f.id === 'FacetSortForm' || f.id === 'FacetFiltersForm' || f.id === 'FacetSortDrawerForm') {
                        forms.push(new URLSearchParams(new FormData(f)).toString());
                    }
                } else if (f.id === 'FacetFiltersFormMobile') {
                    forms.push(new URLSearchParams(new FormData(f)).toString());
                }
            });

            FacetFiltersForm.renderPage(forms.join('&'), null);
        }
    }
    // Close the drawer
    const summary = btn.closest('.mobile-facets__wrapper');
    if (summary) {
        const s = summary.querySelector('summary');
        if (s) s.click();
    }
};